import { useMutation } from '@tanstack/react-query'

import Api from 'utils/api'
import {
  billingSummaryUnavailable,
  normalizeBillingHistory,
  normalizeBillingSummary,
  normalizeInvoicePdf,
  requestBillingApi,
  shouldLoadBillingHistory,
} from 'utils/billing'
import { useSafeQuery } from 'utils/queryClient'

export const billingSummaryQueryKey = ['billing', 'summary']

const loadFallbackAccount = async () => {
  try {
    return await Api.get('/account')
  } catch (_error) {
    return null
  }
}

const loadBillingSummary = async () => {
  try {
    const summary = normalizeBillingSummary(await Api.get('/billing/summary'))
    if (billingSummaryUnavailable(summary)) {
      return { fallbackAccount: await loadFallbackAccount(), summary: null }
    }

    const history = shouldLoadBillingHistory(summary)
      ? normalizeBillingHistory((await Api.get('/billing/history')).history)
      : []

    return { fallbackAccount: null, summary: { ...summary, history } }
  } catch (_error) {
    return { fallbackAccount: await loadFallbackAccount(), summary: null }
  }
}

export const useBillingSummary = () => useSafeQuery(
  billingSummaryQueryKey,
  loadBillingSummary,
  { defaultValue: { fallbackAccount: null, summary: null } },
)

export const useBillingPaymentMutation = options => useMutation({
  mutationFn: async ({ account, isUpgrade }) => normalizeBillingSummary(await requestBillingApi(
    isUpgrade ? 'post' : 'patch',
    isUpgrade ? '/billing/upgrade' : '/billing/payment_method',
    { account },
  )),
  ...options,
})

export const useBillingRenewalMutation = options => useMutation({
  mutationFn: async autoRenewal => normalizeBillingSummary(await requestBillingApi(
    'patch',
    '/billing/renewal',
    { auto_renewal: autoRenewal },
  )),
  ...options,
})

export const useInvoicePdfMutation = options => useMutation({
  mutationFn: async invoiceId => normalizeInvoicePdf(await Api.get(`/billing/invoices/${invoiceId}/pdf`)),
  ...options,
})
